import { createContext, useContext } from "react";

/**
 * Le contexte et son hook, séparés du composant fournisseur.
 *
 * Ce n'est pas du zèle : Vite ne sait rafraîchir un module à chaud que s'il
 * n'exporte que des composants. Mélanger le provider et le hook dans le même
 * fichier casse le rafraîchissement de toute la page à chaque édition d'un
 * drawer d'impression (`react-refresh/only-export-components`).
 */

export type FieldPresenceContextValue = {
  /** Le réglage doit-il être proposé ? `true` en cas de doute. */
  isPresent: (settingKey: string) => boolean;
  /** Le calcul est en cours — tout est proposé en attendant. */
  isLoading: boolean;
  /**
   * `true` quand aucun masquage n'est appliqué : l'utilisateur a demandé à tout
   * voir, ou la présence n'est pas disponible. Sert à ne pas promettre dans
   * l'interface un filtrage qui n'a pas lieu.
   */
  showingEverything: boolean;
  showAllFields: boolean;
  setShowAllFields: (value: boolean) => void;
};

/**
 * Hors fournisseur, tout est proposé : un drawer non encore branché garde
 * exactement son comportement actuel.
 */
export const FIELD_PRESENCE_FALLBACK: FieldPresenceContextValue = {
  isPresent: () => true,
  isLoading: false,
  showingEverything: true,
  showAllFields: false,
  setShowAllFields: () => {},
};

export const FieldPresenceContext = createContext<FieldPresenceContextValue>(
  FIELD_PRESENCE_FALLBACK,
);

export function useFieldPresenceContext(): FieldPresenceContextValue {
  return useContext(FieldPresenceContext);
}
